import type { Labelled } from './common.js';
import { normalizeDigits } from './datamove.js';

/**
 * الحقول المخصَّصة.
 *
 * لكل شركة بيانات تجمعها ولا يعرفها نظام عام: رقم السجل الصناعي، اسم
 * المسؤول في المشتريات، موعد التجديد. بناء عمود لكل طلب مستحيل، وتركها في
 * خانة الملاحظات يجعلها نصًّا لا يُصفّى ولا يدخل تقريرًا ولا يُصدَّر.
 *
 * وهي تخترق حدًّا وُضع في هذا النظام عمدًا: الكتالوجات المكتوبة في الشيفرة
 * هي ما يسمح به محرّك التقارير ومحرّك التصدير، فلا يصل إليهما اسم عمود من
 * المستخدم. الحقول المخصَّصة بيانات لا شيفرة، فتُعامل معاملة القيم لا
 * الأسماء: مفتاح الحقل يُمرَّر وسيطًا في الاستعلام ولا يُدرَج في نصّه، فيبقى
 * الحدّ قائمًا.
 */

/* ==========================================================================
   الكيانات التي تقبل حقولًا
   ========================================================================== */

export const CUSTOM_FIELD_ENTITIES = ['ACCOUNT', 'LEAD'] as const;
export type CustomFieldEntity = (typeof CUSTOM_FIELD_ENTITIES)[number];

export interface CustomFieldEntityDef extends Labelled<CustomFieldEntity> {
  description: string;
  /** الجدول في قاعدة البيانات — لمحرّك التقارير. */
  table: string;
  /** مفتاح المصدر في كتالوج التقارير. */
  reportSource: string;
  /** مفتاح المورد في كتالوج التصدير والاستيراد. */
  dataResource: string;
}

/**
 * سجلّ الكيانات.
 *
 * أسماء الجداول هنا لا تأتي من قاعدة البيانات: هي جزء من الشيفرة، وبقاؤها
 * كذلك هو ما يمنع اسم جدول من الوصول إلى الاستعلام عبر بيانات المستأجر.
 */
export const CUSTOM_FIELD_ENTITY_DEFS: readonly CustomFieldEntityDef[] = [
  {
    key: 'ACCOUNT',
    label: 'العملاء',
    kind: 'info',
    description: 'خانات إضافية في ملف العميل.',
    table: 'accounts',
    reportSource: 'ACCOUNTS',
    dataResource: 'accounts',
  },
  {
    key: 'LEAD',
    label: 'العملاء المحتملون',
    kind: 'info',
    description: 'خانات إضافية في بطاقة العميل المحتمل.',
    table: 'leads',
    reportSource: 'LEADS',
    dataResource: 'leads',
  },
] as const;

export const CUSTOM_FIELD_ENTITY_MAP = Object.fromEntries(
  CUSTOM_FIELD_ENTITY_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CustomFieldEntity, CustomFieldEntityDef>>;

/** اسم عمود القيم. واحد في كل جدول، ومكتوب هنا لا يأتي من الطلب. */
export const CUSTOM_VALUES_COLUMN = 'customFields';

/* ==========================================================================
   الأنواع
   ========================================================================== */

export const CUSTOM_FIELD_TYPES = [
  'TEXT',
  'MULTILINE',
  'NUMBER',
  'DATE',
  'SELECT',
  'BOOLEAN',
  'PHONE',
  'URL',
  'EMAIL',
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export interface CustomFieldTypeDef extends Labelled<CustomFieldType> {
  description: string;
  /** هل يُصفّى به في التقارير كقائمة مغلقة. */
  groupable: boolean;
}

export const CUSTOM_FIELD_TYPE_DEFS: readonly CustomFieldTypeDef[] = [
  {
    key: 'TEXT',
    label: 'نص',
    kind: 'neutral',
    description: 'سطر واحد: اسم، رقم مرجعي، رابط.',
    groupable: true,
  },
  {
    key: 'MULTILINE',
    label: 'نص طويل',
    kind: 'neutral',
    description: 'عدة أسطر. لا يصلح للتقسيم في التقارير.',
    groupable: false,
  },
  {
    key: 'NUMBER',
    label: 'رقم',
    kind: 'info',
    description: 'قيمة عددية تقبل الجمع والمتوسط في التقارير.',
    groupable: true,
  },
  {
    key: 'DATE',
    label: 'تاريخ',
    kind: 'info',
    description: 'تاريخ بصيغة سنة-شهر-يوم.',
    groupable: true,
  },
  {
    key: 'SELECT',
    label: 'قائمة اختيار',
    kind: 'success',
    description: 'خيارات محدَّدة سلفًا. أفضل ما يُقسَّم به تقرير.',
    groupable: true,
  },
  {
    key: 'BOOLEAN',
    label: 'نعم أو لا',
    kind: 'warning',
    description: 'خانة تُعلَّم أو تُترك.',
    groupable: true,
  },
  {
    key: 'PHONE',
    label: 'رقم هاتف',
    kind: 'info',
    description: 'رقمٌ يُعرض باتجاهه الصحيح ويُنقر للاتصال.',
    groupable: false,
  },
  {
    key: 'URL',
    label: 'رابط',
    kind: 'success',
    description: 'رابطٌ يُفتح بنقرة — وروابط السوشيال تُعرض بأيقوناتها.',
    groupable: false,
  },
  {
    key: 'EMAIL',
    label: 'بريد إلكتروني',
    kind: 'info',
    description: 'عنوانُ بريدٍ يُراسَل بنقرة.',
    groupable: false,
  },
] as const;

export const CUSTOM_FIELD_TYPE_MAP = Object.fromEntries(
  CUSTOM_FIELD_TYPE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CustomFieldType, CustomFieldTypeDef>>;

/* ==========================================================================
   الحدود
   ========================================================================== */

/**
 * سقف عدد الحقول لكل كيان.
 *
 * ليس تعسّفًا: كل حقل عمود في التصدير وخانة في النموذج وبُعد في التقارير،
 * وخمسون خانة في نموذج واحد نموذجٌ لا يملؤه أحد. والسقف يحمي كذلك من صفٍّ
 * ينتفخ بمئات المفاتيح.
 */
export const MAX_CUSTOM_FIELDS = 30;
export const MAX_SELECT_OPTIONS = 40;
export const MAX_CUSTOM_TEXT = 2000;

/* ==========================================================================
   التعريف
   ========================================================================== */

export interface CustomFieldDef {
  id: string;
  entity: CustomFieldEntity;
  key: string;
  label: string;
  type: CustomFieldType;
  options: readonly string[];
  required: boolean;
  hint: string | null;
  position: number;
  isActive: boolean;
}

/**
 * توليد مفتاح.
 *
 * لا يُشتقّ من العنوان: العناوين عربية لا تصلح مفاتيحَ، وتُعدَّل بعد إدخال
 * البيانات — والاشتقاق كان يُفقد كل قيمة سُجّلت قبل التعديل. والبادئة تمنع
 * أي التباس مع أسماء أعمدة حقيقية.
 */
export function newCustomFieldKey(random: string): string {
  return `cf_${random.replace(/[^a-z0-9]/gi, '').slice(0, 12).toLowerCase()}`;
}

/** مفتاح مقبول: البادئة وحروف وأرقام فقط. */
export const CUSTOM_FIELD_KEY_PATTERN = /^cf_[a-z0-9]{4,16}$/;

/* ==========================================================================
   التحقق من القيم
   ========================================================================== */

/**
 * ما يلزم للتحقّق فعلًا.
 *
 * الكيانُ والترتيب والمعرّف لا تدخل الحساب، فلا تُطلَب. وبهذا يمرّ على
 * المحقِّق نفسِه ما ليس حقلًا مخصّصًا — حقولُ استمارة التقديم مثلًا،
 * وهي تُملأ من الإنترنت المفتوح فأولى بحدٍّ مختبَرٍ من تحقّقٍ مرتجَل.
 */
export type ValidatableField = Pick<
  CustomFieldDef,
  'key' | 'label' | 'type' | 'options' | 'required' | 'isActive'
>;

export interface CustomValueError {
  key: string;
  label: string;
  message: string;
}

export interface CustomValueResult {
  /** القيم بعد التحويل، جاهزة للتخزين. */
  values: Record<string, string | number | boolean | null>;
  errors: CustomValueError[];
}

/**
 * تحقّق من قيم مُدخلة على تعريفات الحقول.
 *
 * دالة خالصة: هي الحدّ الذي يقف عنده ما يُكتب في عمود JSON، فتُختبر وحدها.
 * المفاتيح غير المعرَّفة تُرفض ولا تُهمَل بصمت — قبولها يجعل الصفّ مستودعًا
 * لأي شيء يُرسله الطلب.
 */
export function validateCustomValues(
  defs: readonly ValidatableField[],
  input: Record<string, unknown> | null | undefined,
  opts: { partial?: boolean } = {},
): CustomValueResult {
  const values: Record<string, string | number | boolean | null> = {};
  const errors: CustomValueError[] = [];
  const raw = input ?? {};

  const known = new Map(defs.map((d) => [d.key, d]));

  for (const key of Object.keys(raw)) {
    if (!known.has(key)) {
      errors.push({ key, label: key, message: 'حقل غير معرَّف.' });
    }
  }

  for (const def of defs) {
    const present = Object.prototype.hasOwnProperty.call(raw, def.key);
    /*
     * التعديل الجزئي لا يمسّ ما لم يُذكر.
     *
     * غياب الحقل في تعديل جزئي يعني «اتركه»، لا «امسحه». الخلط بينهما
     * كان يمسح كل حقل لا يعرضه النموذج الذي أُرسل منه الطلب.
     */
    if (!present) {
      if (opts.partial) continue;
      if (def.required && def.isActive) {
        errors.push({ key: def.key, label: def.label, message: 'هذا الحقل مطلوب.' });
      }
      continue;
    }

    const value = raw[def.key];

    if (value === null || value === undefined || value === '') {
      if (def.required && def.isActive) {
        errors.push({ key: def.key, label: def.label, message: 'هذا الحقل مطلوب.' });
      } else {
        values[def.key] = null;
      }
      continue;
    }

    switch (def.type) {
      case 'NUMBER': {
        const n = Number(normalizeDigits(String(value)).trim());
        if (!Number.isFinite(n)) {
          errors.push({ key: def.key, label: def.label, message: 'القيمة يجب أن تكون رقمًا.' });
        } else {
          values[def.key] = n;
        }
        break;
      }

      case 'BOOLEAN': {
        const t = String(value).trim().toLowerCase();
        if (typeof value === 'boolean') values[def.key] = value;
        else if (['true', 'نعم', '1', 'yes'].includes(t)) values[def.key] = true;
        else if (['false', 'لا', '0', 'no'].includes(t)) values[def.key] = false;
        else {
          errors.push({ key: def.key, label: def.label, message: 'القيمة تكون نعم أو لا.' });
        }
        break;
      }

      case 'DATE': {
        const text = normalizeDigits(String(value)).trim().slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(text) || Number.isNaN(Date.parse(text))) {
          errors.push({
            key: def.key,
            label: def.label,
            message: 'التاريخ بصيغة سنة-شهر-يوم، مثل 2026-08-25.',
          });
        } else {
          values[def.key] = text;
        }
        break;
      }

      case 'SELECT': {
        const text = String(value).trim();
        if (!def.options.includes(text)) {
          errors.push({
            key: def.key,
            label: def.label,
            message: `القيمة ليست من الخيارات: ${def.options.join('، ')}.`,
          });
        } else {
          values[def.key] = text;
        }
        break;
      }

      default: {
        const text = String(value).trim();
        if (text.length > MAX_CUSTOM_TEXT) {
          errors.push({
            key: def.key,
            label: def.label,
            message: `النص أطول من ${MAX_CUSTOM_TEXT} حرفًا.`,
          });
        } else {
          values[def.key] = text;
        }
      }
    }
  }

  return { values, errors };
}

/** عرض قيمة محفوظة نصًّا — للتصدير والطباعة. */
export function formatCustomValue(
  def: CustomFieldDef,
  value: unknown,
): string {
  if (value === null || value === undefined || value === '') return '';
  if (def.type === 'BOOLEAN') return value === true ? 'نعم' : 'لا';
  return String(value);
}
